import { useMemo, useState } from "react";
import {
	CHART_INK,
	colorForSeries,
	formatCompactNumber,
} from "../../lib/palette";
import type { PivotedChart } from "../../lib/pivot";

interface LineChartProps {
	chart: PivotedChart;
	height?: number;
}

const PADDING = { top: 16, right: 12, bottom: 28, left: 44 };

export function LineChart({ chart, height = 220 }: LineChartProps) {
	const [hoverIndex, setHoverIndex] = useState<number | null>(null);
	const width = 640;
	const { categories, series } = chart;

	const maxValue = useMemo(() => {
		let max = 0;
		for (const s of series) for (const p of s.points) max = Math.max(max, p.y);
		return max === 0 ? 1 : max;
	}, [series]);

	if (categories.length === 0 || series.length === 0) {
		return (
			<div className="text-xs font-mono text-base-content/50 py-6 text-center">
				No data for this selection.
			</div>
		);
	}

	const plotWidth = width - PADDING.left - PADDING.right;
	const plotHeight = height - PADDING.top - PADDING.bottom;
	const stepX = categories.length > 1 ? plotWidth / (categories.length - 1) : 0;
	const xFor = (i: number) => PADDING.left + i * stepX;
	const yFor = (v: number) =>
		PADDING.top + plotHeight - (v / maxValue) * plotHeight;

	const yTicks = 4;
	const tickValues = Array.from(
		{ length: yTicks + 1 },
		(_, i) => (maxValue / yTicks) * i,
	);

	const handleMove = (clientX: number, svgEl: SVGSVGElement) => {
		const rect = svgEl.getBoundingClientRect();
		const relX = ((clientX - rect.left) / rect.width) * width;
		const idx = Math.round((relX - PADDING.left) / (stepX || 1));
		setHoverIndex(Math.max(0, Math.min(categories.length - 1, idx)));
	};

	return (
		<div className="relative">
			{series.length > 1 && (
				<div className="flex flex-wrap gap-3 mb-2 px-1">
					{series.map((s, i) => (
						<div
							key={s.name}
							className="flex items-center gap-1.5 text-[10px] font-mono text-base-content/70"
						>
							<span
								className="inline-block w-3 h-0.5 rounded-full"
								style={{ backgroundColor: colorForSeries(s.name, i) }}
							/>
							{s.name}
						</div>
					))}
				</div>
			)}
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="w-full h-auto"
				style={{ maxHeight: height }}
				onPointerMove={(e) => handleMove(e.clientX, e.currentTarget)}
				onPointerLeave={() => setHoverIndex(null)}
			>
				<title>
					Line chart of {series.map((s) => s.name).join(", ")} over time
				</title>
				{tickValues.map((value) => {
					const y = yFor(value);
					return (
						<g key={value}>
							<line
								x1={PADDING.left}
								x2={width - PADDING.right}
								y1={y}
								y2={y}
								stroke={CHART_INK.gridline}
								strokeWidth={1}
							/>
							<text
								x={PADDING.left - 8}
								y={y + 3}
								textAnchor="end"
								fontSize={9}
								fill={CHART_INK.muted}
								fontFamily="monospace"
							>
								{formatCompactNumber(value)}
							</text>
						</g>
					);
				})}
				<line
					x1={PADDING.left}
					x2={width - PADDING.right}
					y1={PADDING.top + plotHeight}
					y2={PADDING.top + plotHeight}
					stroke={CHART_INK.baseline}
					strokeWidth={1}
				/>

				{hoverIndex !== null && (
					<line
						x1={xFor(hoverIndex)}
						x2={xFor(hoverIndex)}
						y1={PADDING.top}
						y2={PADDING.top + plotHeight}
						stroke={CHART_INK.baseline}
						strokeWidth={1}
					/>
				)}

				{series.map((s, si) => {
					const path = s.points
						.map((p, i) => `${i === 0 ? "M" : "L"} ${xFor(i)} ${yFor(p.y)}`)
						.join(" ");
					const color = colorForSeries(s.name, si);
					return (
						<g key={s.name}>
							<path
								d={path}
								fill="none"
								stroke={color}
								strokeWidth={2}
								strokeLinejoin="round"
								strokeLinecap="round"
							/>
							{hoverIndex !== null && s.points[hoverIndex] && (
								<circle
									cx={xFor(hoverIndex)}
									cy={yFor(s.points[hoverIndex].y)}
									r={4}
									fill={color}
									stroke={CHART_INK.surfaceRing}
									strokeWidth={2}
								/>
							)}
						</g>
					);
				})}

				{categories.map((category, i) => {
					if (
						categories.length > 8 &&
						i % Math.ceil(categories.length / 8) !== 0
					)
						return null;
					return (
						<text
							key={category}
							x={xFor(i)}
							y={height - PADDING.bottom + 16}
							textAnchor="middle"
							fontSize={9}
							fill={CHART_INK.muted}
							fontFamily="monospace"
						>
							{category.length > 8 ? `${category.slice(0, 7)}…` : category}
						</text>
					);
				})}
			</svg>
			{hoverIndex !== null && (
				<div
					className="absolute pointer-events-none bg-base-100 border border-base-content/10 rounded px-2 py-1 text-[10px] font-mono shadow-lg z-20"
					style={{
						left: `${Math.min((xFor(hoverIndex) / width) * 100, 78)}%`,
						top: 4,
					}}
				>
					<div className="text-base-content/60 mb-0.5">
						{categories[hoverIndex]}
					</div>
					{series.map((s, i) => (
						<div key={s.name} className="flex items-center gap-1.5">
							<span
								className="inline-block w-2 h-0.5"
								style={{ backgroundColor: colorForSeries(s.name, i) }}
							/>
							<span className="text-base-content font-semibold">
								{formatCompactNumber(s.points[hoverIndex]?.y ?? 0)}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
